import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { forwardRef } from 'react';
import type { LabelProps } from 'semantic-ui-react';
import { Label } from 'semantic-ui-react';

import { getArtifactTypeMetadata } from './artifact-type.const';
import type { ArtifactType } from './artifact-type.models';
import s from './artifact-type-label.scss';

export interface ArtifactTypeLabelProps extends Pick<
  LabelProps,
  'className' | 'size'
> {
  /**
   * The artifact type to display
   */
  type: ArtifactType;
  /**
   * Reduces the label padding for a more compact appearance.
   */
  compact?: boolean;
}

/**
 * A preset label component to show Artifact Types with an icon.
 *
 * @example
 * ```tsx
 * <ArtifactTypeLabel type={ArtifactType.Image} />
 * <ArtifactTypeLabel type={ArtifactType.Source} size="large" compact />
 * ```
 */
export const ArtifactTypeLabel = forwardRef<
  HTMLDivElement,
  ArtifactTypeLabelProps
>(({ className, compact, type, ...labelProps }, forwardedRef) => {
  useStyles(s);

  const { label, icon: Icon } = getArtifactTypeMetadata(type);

  return (
    <Label
      ref={forwardedRef}
      className={classNames(s.root, compact && s.compact, s[type], className)}
      {...labelProps}
    >
      <Icon />
      {label}
    </Label>
  );
});
ArtifactTypeLabel.displayName = 'ArtifactTypeLabel';

export default ArtifactTypeLabel;
